import { vec2 } from 'gl-matrix';

// Reusable buffers to avoid ~5.8MB allocation per call (×520 calls = 3GB GC churn).
// WARNING: These module-level buffers make marchingSquares non-reentrant.
// Do not call marchingSquares from within a callback triggered during
// another marchingSquares invocation.
let _bufWidth = 0;
let _bufHeight = 0;
let _padded: Float64Array | null = null;
let _midX: Float32Array | null = null;
let _midY: Float32Array | null = null;
let _edgeKeys: Int32Array | null = null;
let _slot0: Int32Array | null = null;
let _slot1: Int32Array | null = null;

function ensureBuffers(width: number, height: number) {
    if (width === _bufWidth && height === _bufHeight && _padded) {
        return;
    }
    _bufWidth = width;
    _bufHeight = height;
    const pw = width + 2;
    const ph = height + 2;
    const hStride = pw - 1;
    const numH = ph * hStride;
    const totalSlots = numH + (ph - 1) * pw;
    const maxEdgePairs = (pw - 1) * (ph - 1) * 2;
    _padded = new Float64Array(pw * ph);
    _midX = new Float32Array(totalSlots);
    _midY = new Float32Array(totalSlots);
    _edgeKeys = new Int32Array(maxEdgePairs * 2);
    _slot0 = new Int32Array(totalSlots);
    _slot1 = new Int32Array(totalSlots);
}

/**
 * Trace iso-contour polygons from a 2D scalar grid using
 * the marching squares algorithm.
 *
 * The input grid is assumed to store values sampled at
 * **voxel centers**: the value at index `(i, j)` represents
 * the sample at continuous coordinate `(i + 0.5, j + 0.5)`.
 * Output polygon vertices are in the same continuous
 * coordinate system, so they can be passed directly to
 * `Grid.toWorldSpace()` without any additional offset.
 *
 * @param grid   Flat row-major scalar data (width × height).
 * @param width  Number of columns.
 * @param height Number of rows.
 * @param resx   Horizontal spacing between samples in the
 *               output coordinate system.
 * @param resy   Vertical spacing between samples.
 * @param threshold Iso-value at which to trace contours.
 * @returns Array of closed polygons (each a vec2[] whose
 *          first and last vertex are identical).
 */
export function marchingSquares(
    grid: ArrayLike<number>,
    width: number,
    height: number,
    resx: number,
    resy: number,
    threshold: number
): vec2[][] {
    ensureBuffers(width, height);
    const padded = _padded!;
    const midX = _midX!;
    const midY = _midY!;
    const edgeKeys = _edgeKeys!;

    // Pad grid with 1-pixel zero border to eliminate bounds checks.
    // Original loop: x in [-1, width-1], y in [-1, height-1]
    // Padded: px in [0, width], py in [0, height]
    const pw = width + 2;
    const ph = height + 2;
    // Clear first and last row (border), and first/last column per row
    padded.fill(0, 0, pw); // top border row
    padded.fill(0, (ph - 1) * pw, ph * pw); // bottom border row
    for (let y = 0; y < height; y++) {
        const srcOff = y * width;
        const dstOff = (y + 1) * pw + 1;
        padded[(y + 1) * pw] = 0; // left border
        for (let x = 0; x < width; x++) {
            padded[dstOff + x] = grid[srcOff + x];
        }
        padded[(y + 1) * pw + pw - 1] = 0; // right border
    }

    // Integer addressing for edge midpoints:
    //   Horizontal edge (px→px+1 at row py): key = py * hStride + px
    //   Vertical   edge (py→py+1 at col px): key = numH + py * pw + px
    const hStride = pw - 1;
    const numH = ph * hStride;
    let ek = 0; // write cursor into edgeKeys

    for (let py = 0; py < ph - 1; py++) {
        const rowOff = py * pw;
        const nextRowOff = rowOff + pw;
        // Voxel-center offset (see baseX comment above).
        const baseY = (py - 0.5) * resy;

        for (let px = 0; px < pw - 1; px++) {
            const nw = padded[rowOff + px];
            const ne = padded[rowOff + px + 1];
            const sw = padded[nextRowOff + px];
            const se = padded[nextRowOff + px + 1];

            const caseVal =
                (nw >= threshold ? 8 : 0) |
                (ne >= threshold ? 4 : 0) |
                (se >= threshold ? 2 : 0) |
                (sw >= threshold ? 1 : 0);

            if (caseVal === 0 || caseVal === 15) {
                continue;
            }

            // Midpoint keys
            const ka = py * hStride + px;
            const kb = numH + py * pw + (px + 1);
            const kc = (py + 1) * hStride + px;
            const kd = numH + py * pw + px;

            // Compute all 4 midpoint positions (unconditional — simpler
            // than selective and redundant writes to shared edges are
            // cheaper than per-case branching)
            // Voxel-center offset: sample at index (px-1) has
            // its center at continuous coordinate (px - 0.5).
            const baseX = (px - 0.5) * resx;

            midX[ka] =
                baseX + resx * (ne !== nw ? (threshold - nw) / (ne - nw) : 0.5);
            midY[ka] = baseY;

            midX[kb] = baseX + resx;
            midY[kb] =
                baseY + resy * (se !== ne ? (threshold - ne) / (se - ne) : 0.5);

            midX[kc] =
                baseX + resx * (se !== sw ? (threshold - sw) / (se - sw) : 0.5);
            midY[kc] = baseY + resy;

            midX[kd] = baseX;
            midY[kd] =
                baseY + resy * (sw !== nw ? (threshold - nw) / (sw - nw) : 0.5);

            // Emit edges (directed key pairs)
            switch (caseVal) {
                case 1:
                    edgeKeys[ek++] = kd;
                    edgeKeys[ek++] = kc;
                    break;
                case 14:
                    edgeKeys[ek++] = kc;
                    edgeKeys[ek++] = kd;
                    break;
                case 2:
                    edgeKeys[ek++] = kc;
                    edgeKeys[ek++] = kb;
                    break;
                case 13:
                    edgeKeys[ek++] = kb;
                    edgeKeys[ek++] = kc;
                    break;
                case 3:
                    edgeKeys[ek++] = kd;
                    edgeKeys[ek++] = kb;
                    break;
                case 12:
                    edgeKeys[ek++] = kb;
                    edgeKeys[ek++] = kd;
                    break;
                case 4:
                case 11:
                    edgeKeys[ek++] = kb;
                    edgeKeys[ek++] = ka;
                    break;
                case 5:
                    edgeKeys[ek++] = kd;
                    edgeKeys[ek++] = ka;
                    edgeKeys[ek++] = kb;
                    edgeKeys[ek++] = kc;
                    break;
                case 6:
                    edgeKeys[ek++] = kc;
                    edgeKeys[ek++] = ka;
                    break;
                case 9:
                    edgeKeys[ek++] = ka;
                    edgeKeys[ek++] = kc;
                    break;
                case 7:
                    edgeKeys[ek++] = kd;
                    edgeKeys[ek++] = ka;
                    break;
                case 8:
                    edgeKeys[ek++] = ka;
                    edgeKeys[ek++] = kd;
                    break;
                case 10:
                    edgeKeys[ek++] = ka;
                    edgeKeys[ek++] = kb;
                    edgeKeys[ek++] = kc;
                    edgeKeys[ek++] = kd;
                    break;
            }
        }
    }

    const numEdges = ek >> 1;
    if (numEdges === 0) {
        return [];
    }

    // Build connectivity using flat arrays instead of Map<string, number[]>.
    // Each midpoint lies on a grid edge shared by exactly 2 cells,
    // so it connects at most 2 contour edges.
    const slot0 = _slot0!;
    const slot1 = _slot1!;
    slot0.fill(-1);
    slot1.fill(-1);
    for (let i = 0; i < numEdges; i++) {
        const k0 = edgeKeys[i * 2];
        const k1 = edgeKeys[i * 2 + 1];
        if (slot0[k0] === -1) {
            slot0[k0] = i;
        } else {
            slot1[k0] = i;
        }
        if (slot0[k1] === -1) {
            slot0[k1] = i;
        } else {
            slot1[k1] = i;
        }
    }

    // Chain edges into polygons
    const visited = new Uint8Array(numEdges);
    const polygons: vec2[][] = [];

    for (let i = 0; i < numEdges; i++) {
        if (visited[i]) {
            continue;
        }
        visited[i] = 1;

        const startKey = edgeKeys[i * 2];
        const poly: vec2[] = [vec2.fromValues(midX[startKey], midY[startKey])];

        let curKey = edgeKeys[i * 2 + 1];
        poly.push(vec2.fromValues(midX[curKey], midY[curKey]));

        while (curKey !== startKey) {
            const s0 = slot0[curKey];
            const s1 = slot1[curKey];
            let nextIdx: number;
            if (s0 !== -1 && !visited[s0]) {
                nextIdx = s0;
            } else if (s1 !== -1 && !visited[s1]) {
                nextIdx = s1;
            } else {
                break;
            } // disconnected

            visited[nextIdx] = 1;
            const nk0 = edgeKeys[nextIdx * 2];
            const nk1 = edgeKeys[nextIdx * 2 + 1];
            curKey = curKey === nk0 ? nk1 : nk0;
            poly.push(vec2.fromValues(midX[curKey], midY[curKey]));
        }
        polygons.push(poly);
    }

    return polygons;
}

// line simplification algorithm based on simplify-js

/**
 * Simplifies a given array of points using the Ramer-Douglas-Peucker algorithm.
 */
export function simplify(
    points: vec2[],
    tolerance: number = 1,
    highestQuality: boolean = false
) {
    if (points.length <= 2) {
        return points;
    }

    const sqTolerance = tolerance * tolerance;

    points = highestQuality ? points : simplifyRadialDist(points, sqTolerance);
    points = simplifyDouglasPeucker(points, sqTolerance);

    return points;
}

// square distance between 2 points
function getSqDist(p1: vec2, p2: vec2) {
    const dx = p1[0] - p2[0],
        dy = p1[1] - p2[1];
    return dx * dx + dy * dy;
}

// square distance from a point to a segment
function getSqSegDist(p: vec2, p1: vec2, p2: vec2) {
    let x = p1[0],
        y = p1[1],
        dx = p2[0] - x,
        dy = p2[1] - y;

    if (dx !== 0 || dy !== 0) {
        const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);

        if (t > 1) {
            x = p2[0];
            y = p2[1];
        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }

    dx = p[0] - x;
    dy = p[1] - y;

    return dx * dx + dy * dy;
}

// basic distance-based simplification
function simplifyRadialDist(points: vec2[], sqTolerance: number) {
    if (points.length <= 2) {
        return points;
    }
    let prevPoint = points[0];
    const newPoints = [prevPoint];
    let point!: vec2;

    for (let i = 1, len = points.length; i < len; i++) {
        point = points[i];

        if (getSqDist(point, prevPoint) > sqTolerance) {
            newPoints.push(point);
            prevPoint = point;
        }
    }

    if (prevPoint !== point) {
        newPoints.push(point);
    }

    return newPoints;
}

function simplifyDPStep(
    points: vec2[],
    first: number,
    last: number,
    sqTolerance: number,
    simplified: vec2[]
) {
    let maxSqDist = sqTolerance,
        index!: number;

    for (let i = first + 1; i < last; i++) {
        const sqDist = getSqSegDist(points[i], points[first], points[last]);

        if (sqDist > maxSqDist) {
            index = i;
            maxSqDist = sqDist;
        }
    }

    if (maxSqDist > sqTolerance) {
        if (index - first > 1) {
            simplifyDPStep(points, first, index, sqTolerance, simplified);
        }
        simplified.push(points[index]);
        if (last - index > 1) {
            simplifyDPStep(points, index, last, sqTolerance, simplified);
        }
    }
}

// simplification using Ramer-Douglas-Peucker algorithm
function simplifyDouglasPeucker(points: vec2[], sqTolerance: number) {
    const last = points.length - 1;

    const simplified = [points[0]];
    simplifyDPStep(points, 0, last, sqTolerance, simplified);
    simplified.push(points[last]);

    return simplified;
}
